import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Save, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { exact } from "@/components/RelativeTime";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const card = "rounded-xl border border-slate-200 bg-white shadow-sm p-5";
const SCOPE_LABELS = {
  login: "Login pages (Branch & Merchant; Super Admin is never blocked)",
  public: "Public pages (/payout-request and customer QR page)",
  merchant_api: "Merchant API (/api/v1/*)",
};

function Toggle({ id, label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-4 py-2 cursor-pointer">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch data-testid={id} checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function Stats({ st }) {
  if (!st) return null;
  const items = [["Blocked (24h)", st.blocked_24h], ["Blocked (all time)", st.blocked_total], ["Lookup failures (24h)", st.lookup_failed_24h],
    ["Last blocked IP", st.last_blocked?.ip_address || "-"], ["Last blocked state", st.last_blocked?.state || "-"],
    ["Last blocked at", st.last_blocked ? exact(st.last_blocked.timestamp) : "-"]];
  return (
    <div className={card} data-testid="access-stats">
      <h3 className="font-bold text-slate-900">Blocked attempts</h3>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map(([k, v]) => <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="text-sm font-semibold font-mono text-slate-900 break-all" data-testid={`access-stat-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v ?? "-"}</div></div>)}
      </div>
      <div className="mt-3 text-xs text-slate-500">By scope: {Object.entries(st.by_scope || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
    </div>
  );
}

export function AccessControlPanel() {
  const [s, setS] = useState(null);
  const [allow, setAllow] = useState("");
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/super/access-control").then((r) => { setS(r.data); setAllow((r.data.allowlist || []).join("\n")); }).catch((e) => toast.error(apiError(e)));
    api.get("/super/access-control/stats").then((r) => setSt(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const toggleState = (name) => setS({ ...s, blocked_states: s.blocked_states.includes(name) ? s.blocked_states.filter((x) => x !== name) : [...s.blocked_states, name] });
  const save = async () => {
    setBusy(true);
    try {
      const allowlist = allow.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
      const { data } = await api.put("/super/access-control", { blocked_states: s.blocked_states, block_vpn: s.block_vpn, scopes: s.scopes, allowlist });
      setS(data); setAllow(data.allowlist.join("\n")); toast.success("Access control saved");
      api.get("/super/access-control/stats").then((r) => setSt(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="access-control-panel">
      <div>
        <div className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-teal-600" /><h1 className="text-xl font-bold tracking-tight text-slate-900">Access Control</h1></div>
        <p className="text-sm text-slate-500 mt-1">Block access by Indian state or VPN/proxy. Changes apply immediately.</p>
        {!s.provider_configured && <div data-testid="access-provider-warning" className="mt-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">No IP lookup service is connected yet, so the state and VPN rules can't identify anyone yet. All requests are allowed, and each failed lookup is logged.</div>}
      </div>
      <div className={card}>
        <div className="flex items-center justify-between"><h3 className="font-bold text-slate-900">Blocked states</h3><span className="text-xs text-slate-500" data-testid="blocked-states-count">{s.blocked_states.length} selected</span></div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {s.all_states.map((name) => (
            <label key={name} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox data-testid={`state-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} checked={s.blocked_states.includes(name)} onCheckedChange={() => toggleState(name)} />{name}
            </label>
          ))}
        </div>
      </div>
      <div className={card}>
        <h3 className="font-bold text-slate-900">Rules & scopes</h3>
        <Toggle id="toggle-block-vpn" label="Block VPN / proxy / hosting IPs" checked={s.block_vpn} onChange={(v) => setS({ ...s, block_vpn: v })} />
        <div className="border-t border-slate-100 mt-2 pt-2">
          {Object.entries(SCOPE_LABELS).map(([k, label]) => (
            <Toggle key={k} id={`toggle-scope-${k}`} label={label} checked={!!s.scopes[k]} onChange={(v) => setS({ ...s, scopes: { ...s.scopes, [k]: v } })} />
          ))}
        </div>
        <Label className="text-xs text-slate-500 mt-4 block">Allowlisted IPs (bypass all blocks), one per line</Label>
        <textarea data-testid="access-allowlist-input" value={allow} onChange={(e) => setAllow(e.target.value)} rows={4} className="mt-1 w-full rounded-lg bg-white border border-slate-300 text-slate-900 p-3 text-sm font-mono" placeholder="203.0.113.10" />
        <Button data-testid="access-save" onClick={save} disabled={busy} className="mt-3 bg-teal-600 hover:bg-teal-700 text-white"><Save className="w-4 h-4 mr-1" />{busy ? "Saving..." : "Save"}</Button>
      </div>
      <Stats st={st} />
    </div>
  );
}
